import { Routes } from '@angular/router';
import { AuthGuard, LockGuard } from './core/guards/auth.guard';
import { gdpAccessGuard, gdpAdminGuard } from '@features/gdp/guards/gdp-access.guard';
import { legacySprintRedirectGuard } from '@features/gdp/guards/legacy-sprint-redirect.guard';
import { kanbanProjectRedirectGuard } from '@features/gdp/guards/kanban-project-redirect.guard';
import { hasPermissionGuard, hasToolPermissionGuard } from '@core/guards/permission.guard';
import { PERMISSIONS } from '@core/services/permission/permission.model';

export const routes: Routes = [
  {
    path: 'deca/edit/:token',
    loadComponent: () => import('@features/deca/deca-public-edit.component').then(m => m.DecaPublicEditComponent)
  },
  {
    path: 'deca',
    loadComponent: () => import('@features/deca/deca.component').then(m => m.DecaComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_DECA)]
  },
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'login',
    loadComponent: () => import('@features/auth/pages/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'lock',
    loadComponent: () => import('@features/auth/pages/lock/lock.component').then(m => m.LockComponent),
    canActivate: [LockGuard]
  },
  {
    path: 'register',
    loadComponent: () => import('@features/auth/pages/register/register.component').then(m => m.RegisterComponent)
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('@features/auth/pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },
  {
    path: 'set-password',
    loadComponent: () => import('@features/auth/pages/set-password/set-password.component').then(m => m.SetPasswordComponent)
  },
  {
    path: 'users',
    loadComponent: () => import('@features/users/pages/users/users.component').then(m => m.UsersComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.USER_MANAGE, PERMISSIONS.MANAGE_ROLES)]
  },
  {
    path: 'dashboard',
    loadComponent: () => import('@features/dashboard/pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'directory',
    redirectTo: '/directory/clients',
    pathMatch: 'full'
  },
  {
    path: 'directory/:tab',
    loadComponent: () => import('@features/clients/pages/directory/directory.component').then(m => m.DirectoryComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_DIRECTORY)]
  },
  {
    path: 'transport-orders',
    loadComponent: () => import('@features/transport-orders/pages/transport-orders/transport-orders.component').then(m => m.TransportOrdersComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_ORDERS)]
  },
  {
    path: 'transport-quotes',
    loadComponent: () => import('@features/transport-quotes/pages/transport-quotes/transport-quotes.component').then(m => m.TransportQuotesComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_QUOTES)]
  },
  {
    path: 'shipments',
    loadComponent: () => import('@features/shipments/pages/shipments/shipments.component').then(m => m.ShipmentsComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_SHIPMENT)]
  },
  {
    path: 'billing',
    redirectTo: '/billing/invoices',
    pathMatch: 'full'
  },
  {
    path: 'billing/:tab',
    loadComponent: () => import('@features/invoices/pages/invoices/invoices.component').then(m => m.InvoicesComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_INVOICES)]
  },
  {
    path: 'invoices',
    redirectTo: '/billing/invoices',
    pathMatch: 'full'
  },
  {
    path: 'income',
    loadComponent: () => import('@features/finances/pages/income/income.component').then(m => m.IncomeComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_INCOMES)]
  },
  {
    path: 'expenses',
    loadComponent: () => import('@features/finances/pages/expenses/expenses.component').then(m => m.ExpensesComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_EXPENSES)]
  },
  {
    path: 'commissions',
    loadComponent: () => import('@features/commissions/pages/commissions/commissions.component').then(m => m.CommissionsComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_COMMISSIONS)]
  },
  {
    path: 'profile',
    loadComponent: () => import('@features/auth/pages/profile/profile.component').then(m => m.ProfileComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'gdp',
    loadComponent: () => import('@features/gdp/pages/gestion/gestion.component').then(m => m.GestionComponent),
    canActivate: [AuthGuard, gdpAccessGuard]
  },
  {
    path: 'gdp/toolbox',
    loadComponent: () => import('@features/gdp/pages/toolbox/toolbox.component').then(m => m.ToolboxComponent),
    canActivate: [AuthGuard, gdpAccessGuard, hasToolPermissionGuard]
  },
  {
    path: 'gdp/toolbox/csv-to-mail',
    loadComponent: () => import('@features/gdp/pages/csv-to-mail/csv-to-mail.component').then(m => m.CsvToMailComponent),
    canActivate: [AuthGuard, gdpAccessGuard, hasPermissionGuard(PERMISSIONS.CSV_MAIL_TOOL)]
  },
  {
    path: 'gdp/suggestions',
    loadComponent: () => import('@features/gdp/pages/suggestions-admin/suggestions-admin.component').then(m => m.SuggestionsAdminComponent),
    canActivate: [AuthGuard, gdpAdminGuard]
  },
  {
    path: 'gdp/projects/:projectId',
    loadComponent: () => import('@features/gdp/pages/project-detail/project-detail.component').then(m => m.ProjectDetailComponent),
    canActivate: [AuthGuard, gdpAccessGuard]
  },
  {
    path: 'gdp/backlogs/:backlogId',
    loadComponent: () => import('@features/gdp/pages/backlog-detail/backlog-detail.component').then(m => m.BacklogDetailComponent),
    canActivate: [AuthGuard, gdpAccessGuard]
  },
  {
    path: 'gdp/projects/:projectId/sprints/:sprintId/plan',
    loadComponent: () => import('@features/gdp/pages/sprint-planning/sprint-planning.component').then(m => m.SprintPlanningComponent),
    canActivate: [AuthGuard, gdpAccessGuard]
  },
  {
    path: 'gdp/projects/:projectId/sprints/:sprintId/board',
    loadComponent: () => import('@features/gdp/pages/sprint-board/sprint-board.component').then(m => m.SprintBoardComponent),
    // Los tableros de proyectos KANBAN viven en /gdp/projects/:projectId:
    // el guard redirige allí (conservando ?task=/?us=); SCRUM pasa tal cual
    canActivate: [AuthGuard, gdpAccessGuard, kanbanProjectRedirectGuard]
  },
  // Rutas antiguas sin proyecto: el guard carga el sprint y redirige a la nueva URL
  {
    path: 'gdp/sprints/:sprintId/plan',
    canActivate: [AuthGuard, legacySprintRedirectGuard],
    children: []
  },
  {
    path: 'gdp/sprints/:sprintId/board',
    canActivate: [AuthGuard, legacySprintRedirectGuard],
    children: []
  },
  {
    path: 'notifications/preferences',
    loadComponent: () => import('@features/notifications/pages/preferences/notification-preferences.component').then(m => m.NotificationPreferencesComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'suggestions',
    loadComponent: () => import('@features/gdp/pages/suggestions/suggestions.component').then(m => m.SuggestionsComponent),
    canActivate: [AuthGuard, hasPermissionGuard(PERMISSIONS.ACCESS_SUGGESTIONS)]
  },
  {
    path: '**',
    redirectTo: '/login'
  }
];
